import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAssetHealthOverview } from '@/hooks/use-asset-management-widgets';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Heart, PieChart as PieChartIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Asset Health Overview Widget - Displays health distribution with colored donut chart
// Colors for health conditions
const HEALTH_COLORS: Record<string, string> = {
  'excellent': '#10b981', // green-500
  'good': '#3b82f6', // blue-500
  'fair': '#f59e0b', // amber-500
  'poor': '#ef4444', // red-500
  'critical': '#991b1b', // red-800
  // Current asset status types
  'inservice': '#10b981', // green-500 - In Service
  'outofservice': '#94a3b8', // slate-400 - Out of Service
  'disposed': '#dc2626', // red-600 - Disposed
  'fullydepreciated': '#8b5cf6', // violet-500 - Fully Depreciated
  'pending': '#a855f7', // purple-500 - Pending / On Order
  'undermaintenance': '#f59e0b', // amber-500 - Under Maintenance
  'reserved': '#06b6d4', // cyan-500 - Reserved
  'lostmissing': '#ef4444', // red-500 - Lost / Missing
  'leased': '#3b82f6', // blue-500 - Leased
  // Legacy status types for backwards compatibility
  'inactive': '#94a3b8', // slate-400
  'active': '#10b981', // green-500
  'maintenance': '#f59e0b', // amber-500
  'retired': '#64748b', // slate-500
  'unknown': '#6b7280', // gray-500
};

// Helper function to convert status to title case
const toTitleCase = (str: string): string => {
  return str
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Custom tooltip component for the pie chart - moved outside to avoid re-creation on each render
const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { name: string; value: number; percentage: number } }> }) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold">{data.name}</p>
        <p className="text-sm text-muted-foreground">
          {data.value} assets ({data.percentage.toFixed(1)}%)
        </p>
      </div>
    );
  }
  return null;
};

export function AssetHealthOverviewWidget() {
  const { data, isLoading, error } = useAssetHealthOverview();

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
              <CardDescription>Overall asset health distribution</CardDescription>
            </div>
            <div className="p-2 bg-teal-50 dark:bg-teal-950 rounded-lg">
              <Heart className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Handle error state (API failure)
  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
              <CardDescription>Overall asset health distribution</CardDescription>
            </div>
            <div className="p-2 bg-teal-50 dark:bg-teal-950 rounded-lg">
              <Heart className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Unable to load asset health data
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data state (no assets in database)
  if (!data?.healthDistribution?.length) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
              <CardDescription>Overall asset health distribution</CardDescription>
            </div>
            <div className="p-2 bg-teal-50 dark:bg-teal-950 rounded-lg">
              <Heart className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col h-[300px] items-center justify-center text-center">
            <PieChartIcon className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No asset health data available
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data for pie chart with explicit colors
  const chartData = data.healthDistribution?.map((item: { status: string; count: number; percentage: number; color?: string }) => ({
    name: toTitleCase(item.status),
    value: item.count,
    percentage: item.percentage,
    color: item.color ?? HEALTH_COLORS[item.status.toLowerCase()] ?? '#6b7280',
  })) ?? [];

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
            <CardDescription>Overall asset health distribution</CardDescription>
          </div>
          <div className="p-2 bg-teal-50 dark:bg-teal-950 rounded-lg">
            <Heart className="h-5 w-5 text-teal-600 dark:text-teal-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1 flex flex-col justify-center">
          {/* Chart */}
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {chartData.map((entry) => (
                  <Cell 
                    key={`cell-${entry.name}`} 
                    fill={entry.color}
                    stroke="none"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            {chartData.map((item) => (
              <div key={`legend-${item.name}`} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{item.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.value} ({item.percentage.toFixed(1)}%)
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total Assets at Bottom */}
        <div className="pt-4 mt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Assets</span>
            <span className="text-2xl font-bold">{data.totalAssets}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
